import { adminSupabase } from '@/lib/supabase'
import { resendInviteApi, type LeadTemplate, type ResendChannel } from '@/api/resendInvite'
import type { Lead, LeadStatus } from '@/types'

type LeadRow = {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  business_name: string | null
  source: string
  status: LeadStatus
  notes: string | null
  converted_org_id: string | null
  created_at: string
  updated_at: string
}

const mapLeadRow = (row: LeadRow): Lead => ({
  id: row.id,
  name: [row.first_name, row.last_name].filter(Boolean).join(' ').trim(),
  email: row.email,
  phone: row.phone,
  company_name: row.business_name,
  message: null,
  source: row.source as Lead['source'],
  status: row.status,
  notes: row.notes,
  converted_org_id: row.converted_org_id,
  converted_org: null,
  created_at: row.created_at,
  updated_at: row.updated_at,
})

export const leadsApi = {
  list: async (params: {
    status?: LeadStatus | 'all'
    search?: string
  } = {}): Promise<Lead[]> => {
    let query = adminSupabase
      .from('jumpstart_leads')
      .select('*')
      .order('created_at', { ascending: false })

    if (params.status && params.status !== 'all') {
      query = query.eq('status', params.status)
    }
    if (params.search) {
      query = query.or(
        `first_name.ilike.%${params.search}%,last_name.ilike.%${params.search}%,email.ilike.%${params.search}%,business_name.ilike.%${params.search}%,phone.ilike.%${params.search}%`
      )
    }

    const { data, error } = await query
    if (error) throw error
    return ((data ?? []) as LeadRow[]).map(mapLeadRow)
  },

  get: async (id: string): Promise<Lead> => {
    const { data, error } = await adminSupabase
      .from('jumpstart_leads')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return mapLeadRow(data as LeadRow)
  },

  create: async (lead: Partial<Lead>): Promise<Lead> => {
    const [firstName, ...rest] = (lead.name ?? '').trim().split(' ')
    const lastName = rest.join(' ').trim()
    const { data, error } = await adminSupabase
      .from('jumpstart_leads')
      .insert({
        first_name: firstName || null,
        last_name: lastName || null,
        email: lead.email ?? null,
        phone: lead.phone ?? null,
        business_name: lead.company_name ?? null,
        source: lead.source ?? 'manual',
        status: lead.status ?? 'new',
        notes: lead.notes ?? null,
        converted_org_id: lead.converted_org_id ?? null,
      })
      .select()
      .single()
    if (error) throw error
    return mapLeadRow(data as LeadRow)
  },

  update: async (id: string, updates: Partial<Lead>): Promise<Lead> => {
    const [firstName, ...rest] = (updates.name ?? '').trim().split(' ')
    const lastName = rest.join(' ').trim()
    const patch: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (updates.name !== undefined) {
      patch.first_name = firstName || null
      patch.last_name = lastName || null
    }
    if (updates.email !== undefined) patch.email = updates.email
    if (updates.phone !== undefined) patch.phone = updates.phone
    if (updates.company_name !== undefined) patch.business_name = updates.company_name
    if (updates.source !== undefined) patch.source = updates.source
    if (updates.status !== undefined) patch.status = updates.status
    if (updates.notes !== undefined) patch.notes = updates.notes
    if (updates.converted_org_id !== undefined) patch.converted_org_id = updates.converted_org_id

    const { data, error } = await adminSupabase
      .from('jumpstart_leads')
      .update(patch)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return mapLeadRow(data as LeadRow)
  },

  updateStatus: async (id: string, status: LeadStatus): Promise<Lead> => {
    return leadsApi.update(id, { status })
  },

  convertToOrg: async (leadId: string, orgName: string): Promise<{ lead: Lead; orgId: string }> => {
    throw new Error(
      `convertToOrg is deprecated (${orgName}). Use ConvertWizard jumpstart-onboard-org flow instead for lead ${leadId}.`
    )
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await adminSupabase
      .from('jumpstart_leads')
      .delete()
      .eq('id', id)
    if (error) throw error
  },

  getStats: async (): Promise<Record<LeadStatus, number>> => {
    const { data, error } = await adminSupabase
      .from('jumpstart_leads')
      .select('status')

    if (error) throw error

    const counts: Record<string, number> = { new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 }
    for (const row of data ?? []) {
      counts[row.status] = (counts[row.status] || 0) + 1
    }
    return counts as Record<LeadStatus, number>
  },

  resendLeadInvite: async (
    leadId: string,
    channel: ResendChannel,
    templateId: string
  ): Promise<{ sent_to: string }> => {
    return resendInviteApi.send({
      target_type: 'lead',
      target_id: leadId,
      channel,
      template_id: templateId,
    })
  },

  listLeadTemplates: async (): Promise<LeadTemplate[]> => {
    return resendInviteApi.listLeadTemplates()
  },
}
